<template>
	<section class="view-signup-favoriteplaces">
		<header class="stepped-overlay-header">
			<ChalkyBrandingBadge size="md" />
			<h3>Where do you play?</h3>
			<p>Select your favorite places</p>
		</header>

		<section class="fields stepped-overlay-content" v-bind:key="selectedVenues">
			<ChalkyVenueCardPreview
				v-bind:class="{ 'state-selected': selectedVenues[venueModel.id] }"
				v-bind:key="venueModel.id"
				v-bind:venueModel="venueModel"
				v-for="venueModel in venueCollection"
				v-on:click.native="Handle_OnClickVenue($event, venueModel)"
			/>
		</section>

		<section class="actions stepped-overlay-actions">
			<button class="skip button-secondary" v-on:click="Handle_OnClickSkip">Skip</button>
			<button class="continue button-primary" v-on:click="Handle_OnClickContinue">Let's go!</button>
		</section>
	</section>
</template>

<script lang="ts">
	import * as Factory from '@/App/Factory';
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Vue } from 'vue-property-decorator';
	import { beforeDestroy, bind, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Signup
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewSignupFavoritePlaces extends ViewBase {
		/**
		 * @type ChalkySticks.Model.User
		 */
		@Prop()
		protected userModel!: ChalkySticks.Model.User;

		/**
		 * @type ChalkySticks/Collection/Venue
		 */
		@Prop({
			default: () => Factory.Venue.collection({ limit: 6 }),
		})
		protected venueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * @type Record<string, boolean>
		 */
		protected selectedVenues: Record<string, boolean> = {};

		/**
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			await this.venueCollection.fetch();

			this.venueCollection.each((venueModel: ChalkySticks.Model.Venue) => {
				this.selectedVenues[venueModel.id] = false;
			});
		}

		/**
		 * Navigates to the next step in the signup process
		 *
		 * @return void
		 */
		protected next(): void {
			this.$router.push({ params: { step: 'confirmation' } });
		}

		/**
		 * Skip to the app
		 *
		 * @return void
		 */
		protected skip(): void {
			this.$router.push({ params: { step: 'confirmation' } });
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			// Iterate selected venues
			this.venueCollection.each((venueModel: ChalkySticks.Model.Venue) => {
				// If the venue is selected, add it to the user's favorite places
				if (this.selectedVenues[venueModel.id]) {
					venueModel.reactions.auth().favorite();
				}
			});

			// If validation passes, proceed to next step
			this.next();
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		public async Handle_OnClickSkip(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.skip();
		}

		/**
		 * @param Event e
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnClickVenue(e: Event, venueModel: ChalkySticks.Model.Venue): Promise<void> {
			e.preventDefault();

			// Toggle
			Vue.set(this.selectedVenues, venueModel.id, !this.selectedVenues[venueModel.id]);

			this.$forceUpdate();
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-signup-favoriteplaces {
		.fields {
			display: grid;
			gap: 1rem;
			grid-auto-rows: min-content;
			grid-template-columns: 1fr 1fr;
			margin: 2rem 0;

			.venue-cardpreview {
				--padding: 0.75rem;

				background-color: var(--chalky-blue);
				// border: 4px solid transparent;

				.content .name {
					font-size: 1rem;
					max-width: 100%;
				}

				.utility-dots,
				.actions,
				.address,
				.distance {
					display: none;
				}
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.view-signup-favoriteplaces .venue-cardpreview.state-selected {
		outline: 4px solid var(--chalky-white);
	}
</style>
